import { MerchantCashAdvance, Syndication, SyndicationCalculation, User } from '../../graphql/types';
import { Box, Divider, Grid, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Stack, Typography } from '@mui/material';
import { DollarCircleOutlined, DollarOutlined, LineOutlined, PercentageOutlined, SelectOutlined } from '@ant-design/icons';
import React from 'react';
import { capitalizeFirstLetter } from '../../utils/strings';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import { PercentText } from '../../components/input-formatters/PercentText';
import MainCard from '../../components/MainCard';
import Decimal from 'decimal.js';
import { useTheme } from '@mui/material/styles';
import { getUserProfileFullName } from 'utils/users';

interface Props {
  mca: MerchantCashAdvance;
  syndication: Syndication;
}

const SyndicationDetails = ({ syndication, mca }: Props) => {
  const theme = useTheme();

  return (
    <Box p={2}>
      <Grid container rowSpacing={1} columnSpacing={1}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <MainCard border={false} shadow={3} boxShadow sx={{ height: 280 }}>
            <Grid item xs={12}>
              <Stack spacing={2.5} alignItems="center">
                <Stack spacing={0.5} alignItems="center">
                  <Typography color="#706ce6">SYNDICATION INFO</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <List component="nav" aria-label="SYNDICATION INFO" sx={{ py: 0, '& .MuiListItem-root': { p: 0 } }}>
                <ListItem>
                  <ListItemIcon>
                    <LineOutlined style={{ color: '#706ce6' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Syndicator</Typography>} />
                </ListItem>
                <ListItem sx={{ marginBottom: 2 }}>
                  <ListItemText
                    primary={
                      <Stack component="span" direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography
                          color="secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {getUserProfileFullName(syndication?.user as User)}
                        </Typography>
                        <Typography component="span" color="primary.main" variant="caption">
                          {syndication?.user?.role?.join(' / ')}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <SelectOutlined style={{ color: '#706ce6' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Syndication type</Typography>} />
                  <ListItemSecondaryAction>
                    <ListItemText primary={<Typography>{capitalizeFirstLetter(syndication.type)}</Typography>} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarOutlined style={{ color: '#706ce6' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Money In</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={syndication.amount} /> (
                    <PercentText value={calculateSyndicationPercent(mca, syndication)} />)
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarOutlined style={{ color: '#706ce6' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Payback</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={mca.payback * (syndication.amount / mca.amount)} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#13C2C2' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Net Invested</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={calculateSyndicationNetInvested(syndication)} />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Grid>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <MainCard border={false} shadow={3} boxShadow sx={{ height: 280 }}>
            <Grid item xs={12}>
              <Stack spacing={2.5} alignItems="center">
                <Stack spacing={0.5} alignItems="center">
                  <Typography>COMMISSIONS & FEES</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <List component="nav" aria-label="COMMISSIONS" sx={{ py: 0, '& .MuiListItem-root': { p: 0 } }}>
                <ListItem>
                  <ListItemIcon>
                    <PercentageOutlined style={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">MY BRK COMM</Typography>} />
                  <ListItemSecondaryAction>
                    <PercentText value={syndication.brkCommissionPercentage} /> (
                    <CurrencyText value={(syndication.amount * syndication.brkCommissionPercentage) / 100} />)
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PercentageOutlined style={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">MY ISO COMM</Typography>} />
                  <ListItemSecondaryAction>
                    <PercentText value={syndication.isoCommissionPercentage} /> (
                    <CurrencyText value={(syndication.amount * syndication.isoCommissionPercentage) / 100} />)
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PercentageOutlined style={{ color: theme.palette.error.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">MY ORG FEE</Typography>} />
                  <ListItemSecondaryAction>
                    <PercentText value={syndication.orgFeeCommissionPercentage} /> (
                    <CurrencyText value={(syndication.amount * syndication.orgFeeCommissionPercentage) / 100} />)
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Grid>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <MainCard border={false} shadow={3} boxShadow sx={{ height: 280 }}>
            <Grid item xs={12}>
              <Stack spacing={2.5} alignItems="center">
                <Stack spacing={0.5} alignItems="center">
                  <Typography color="#FF7A45">FEES</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <List component="nav" aria-label="FEES" sx={{ py: 0, '& .MuiListItem-root': { p: 0 } }}>
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#FF7A45' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Wire Fee</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={syndication.wireFee} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PercentageOutlined style={{ color: '#FF7A45' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Service Fee</Typography>} />
                  <ListItemSecondaryAction>
                    <PercentText value={syndication.serviceFeePercentage} /> (
                    <CurrencyText value={calculateSyndicationServiceAmount(mca, syndication)} />)
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#FF7A45' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Crm Fee</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={syndication.crmFee} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#FF7A45' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Other Fee</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={syndication.otherFee} />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Grid>
          </MainCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SyndicationDetails;

export function getMaxSyndicationMoneyIn(maxNetAmount: number, syndication: Syndication | any) {
  if (syndication.calculationType !== SyndicationCalculation.Net) {
    return maxNetAmount;
  }
  const fixedFees = new Decimal(syndication.crmFee || 0).add(syndication.wireFee || 0).add(syndication.otherFee || 0);
  const brkAndIsoCommissionRate = new Decimal(syndication.isoCommissionPercentage || 0)
    .add(syndication.brkCommissionPercentage || 0)
    .div(100);
  const orgFeeCommissionRate = new Decimal(syndication.orgFeeCommissionPercentage || 0).div(100);
  const percentageModifier = orgFeeCommissionRate.sub(brkAndIsoCommissionRate);
  const maxMoneyIn = new Decimal(maxNetAmount).add(fixedFees).div(new Decimal(1).add(percentageModifier));
  return maxMoneyIn.toNumber();
}

export function calculateSyndicationNetInvested(syndication: Syndication | any) {
  if (SyndicationCalculation.Net === syndication.calculationType) {
    return new Decimal(syndication.amount || 0)
      .sub(syndication.crmFee || 0)
      .sub(syndication.wireFee || 0)
      .sub(syndication.otherFee || 0)
      .add(((syndication.amount || 0) * (syndication.orgFeeCommissionPercentage || 0)) / 100)
      .sub(
        new Decimal(syndication.isoCommissionPercentage || 0)
          .add(syndication.brkCommissionPercentage || 0)
          .div(100)
          .mul(syndication.amount || 0)
      )
      .toNumber();
  }
  return syndication.amount;
}

export function calculateSyndicationMoneyIn(syndication: Syndication | any) {
  if (SyndicationCalculation.Gross === syndication.calculationType) {
    const amount = new Decimal(syndication.amount || 0);

    // Add fixed fees
    const fixedFees = new Decimal(syndication.crmFee || 0)
      .add(syndication.wireFee || 0)
      .add(syndication.otherFee || 0);

    // Calculate percentage-based commissions
    const brkAndIsoCommissions = new Decimal(syndication.isoCommissionPercentage || 0)
      .add(syndication.brkCommissionPercentage || 0)
      .div(100)
      .mul(amount);

    // Calculate origination fee
    const orgFeeCommission = amount
      .mul(syndication.orgFeeCommissionPercentage || 0)
      .div(100);

    // For GROSS calculation, Money In = amount + all fees - org fee
    return amount
      .add(fixedFees)
      .add(brkAndIsoCommissions)
      .sub(orgFeeCommission)
      .toNumber();
  }
  // For NET calculation, Money In is just the amount
  return syndication.amount;
}

export function calculateSyndicationPercent(mca: MerchantCashAdvance, syndication: Syndication | any) {
  return new Decimal(calculateSyndicationNetInvested(syndication)).div(mca.amount).mul(100).toNumber();
}

export function calculateSyndicationServiceAmount(mca: MerchantCashAdvance, syndication: Syndication) {
  return new Decimal(syndication.amount).mul(syndication.serviceFeePercentage / 100).toNumber();
}

export function calculateSyndicationPayback(mca: MerchantCashAdvance, syndication: Syndication | any) {
  return calculateSyndicationNetInvested(syndication) * mca.factorRate;
}

export function calculateSyndicationPnlNet(mca: MerchantCashAdvance, syndication: Syndication | any) {
  let netPayback = calculateSyndicationPayback(mca, syndication);
  return netPayback - calculateSyndicationNetInvested(syndication) - ((netPayback || 0) * syndication.serviceFeePercentage) / 100;
}

export function calculateSyndicationPnlGross(mca: MerchantCashAdvance, syndication: Syndication | any) {
  return (syndication.amount || 0) * mca.factorRate - (syndication.amount || 0);
}
